import { Clock, Users } from "lucide-react";
import React from "react";

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../../../../components/ui/tooltip";
import { JobApplicationStatus } from "../../../../handlers/enums";

function SummarySection({ applications = [], handleFilterChange, statusFilter }) {
	// Helper functions for the summary section
	const countApplicationsByStatus = (applications) => {
		const counts = {
			[JobApplicationStatus.APPLIED]: 0,
			[JobApplicationStatus.SCREENING]: 0,
			[JobApplicationStatus.RECRUITER_CALL]: 0,
			[JobApplicationStatus.INTERVIEW]: 0,
			[JobApplicationStatus.REJECTED]: 0,
			[JobApplicationStatus.ORAL_INTERVIEW]: 0,
			[JobApplicationStatus.TEAM_INTERVIEW]: 0,
			[JobApplicationStatus.TECHNICAL_INTERVIEW]: 0,
		};

		applications.forEach((application) => {
			const status = application.stages[application.stages.length - 1]?.status;
			counts[status] = (counts[status] || 0) + 1;
		});

		return counts;
	};

	const countApplicationsAddedToday = (applications) => {
		const today = new Date();
		today.setHours(0, 0, 0, 0);

		return applications.filter((application) => {
			const applicationDate = new Date(application.createdOn);
			applicationDate.setHours(0, 0, 0, 0);
			return applicationDate.getTime() === today.getTime();
		}).length;
	};

	const countInterviews = (applications) => {
		return applications
			.map((application) =>
				application.stage && application.stage.length > 0 ? application.stage[application.stage.length - 1] : null,
			)
			.filter(
				(update) =>
					update &&
					(update.status === JobApplicationStatus.INTERVIEW ||
						update.status === JobApplicationStatus.RECRUITER_CALL ||
						update.status === JobApplicationStatus.ORAL_INTERVIEW ||
						update.status === JobApplicationStatus.TEAM_INTERVIEW ||
						update.status === JobApplicationStatus.TECHNICAL_INTERVIEW),
			).length;
	};

	// Calculate summary metrics
	const applicationsByStatus = countApplicationsByStatus(applications);
	const applicationsAddedToday = countApplicationsAddedToday(applications);
	const interviews = countInterviews(applications);

	// Calculate percentages for the status distribution bar
	const totalApplications = applications.length;
	const appliedCount = applicationsByStatus[JobApplicationStatus.APPLIED];
	const screeningCount = applicationsByStatus[JobApplicationStatus.SCREENING];
	const interviewCount =
		applicationsByStatus[JobApplicationStatus.RECRUITER_CALL] +
		applicationsByStatus[JobApplicationStatus.INTERVIEW] +
		applicationsByStatus[JobApplicationStatus.ORAL_INTERVIEW] +
		applicationsByStatus[JobApplicationStatus.TEAM_INTERVIEW] +
		applicationsByStatus[JobApplicationStatus.TECHNICAL_INTERVIEW];
	const rejectionCount = applicationsByStatus[JobApplicationStatus.REJECTED]; // Using REJECTED for now could add OFFER status later

	const appliedPercentage = totalApplications > 0 ? (appliedCount / totalApplications) * 100 : 0;
	const screeningPercentage = totalApplications > 0 ? (screeningCount / totalApplications) * 100 : 0;
	const interviewPercentage = totalApplications > 0 ? (interviewCount / totalApplications) * 100 : 0;
	const rejectionPercentage = totalApplications > 0 ? (rejectionCount / totalApplications) * 100 : 0;

	const handleStatusBarClick = (statusType) => {
		switch (statusType) {
			case "APPLIED":
				// Applied bar combines BOOKMARKED and APPLIED statuses
				handleFilterChange(JobApplicationStatus.APPLIED);
				break;
			case "SCREENING":
				// Screening bar combines SCREENING status
				handleFilterChange(JobApplicationStatus.SCREENING);
				break;
			case "INTERVIEW":
				// Interview bar combines RECRUITER_CALL and all INTERVIEW statuses
				handleFilterChange(JobApplicationStatus.INTERVIEW);
				break;
			case "REJECTED":
				handleFilterChange(JobApplicationStatus.REJECTED);
				break;
			default:
				handleFilterChange(null);
		}
	};

	return (
		<div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5 mb-12">
			{/* Total Applications Card */}
			<div className="bg-white p-4 rounded-lg border">
				<div className="flex justify-between items-center">
					<div>
						<h2 className="text-sm text-gray-500 font-medium">Total Applications</h2>
						<div className="flex items-end gap-2">
							<p className="text-3xl font-bold">{totalApplications}</p>
						</div>
						{applicationsAddedToday > 0 && (
							<p className="text-green-600 font-medium text-sm pb-1">+ {applicationsAddedToday} today</p>
						)}
					</div>
					<div className="bg-blue-100 p-2 rounded-full">
						<Users className="text-blue-600 w-5 h-5" />
					</div>
				</div>
			</div>

			{/* Stage Distribution Card */}
			<div className="bg-white p-4 rounded-lg border">
				<h2 className="text-sm text-gray-500 font-medium mb-2">Stage Distribution</h2>
				<div className="flex h-6 w-full rounded-lg overflow-hidden">
					<TooltipProvider delayDuration={0}>
						{appliedCount > 0 && (
							<Tooltip>
								<TooltipTrigger asChild>
									<div
										className={`bg-blue-300 cursor-pointer ${statusFilter === JobApplicationStatus.APPLIED ? "ring-2 ring-blue-500" : ""}`}
										style={{ width: `${appliedPercentage}%` }}
										onClick={() => handleStatusBarClick("APPLIED")}
									/>
								</TooltipTrigger>
								<TooltipContent className="px-2 py-1 text-xs" showArrow={true}>
									Applied: {appliedCount}
								</TooltipContent>
							</Tooltip>
						)}
						{screeningCount > 0 && (
							<Tooltip>
								<TooltipTrigger asChild>
									<div
										className={`bg-purple-300 cursor-pointer ${statusFilter === JobApplicationStatus.SCREENING ? "ring-2 ring-purple-500" : ""}`}
										style={{ width: `${screeningPercentage}%` }}
										onClick={() => handleStatusBarClick("SCREENING")}
									/>
								</TooltipTrigger>
								<TooltipContent className="px-2 py-1 text-xs" showArrow={true}>
									Screening: {screeningCount}
								</TooltipContent>
							</Tooltip>
						)}
						{interviewCount > 0 && (
							<Tooltip>
								<TooltipTrigger asChild>
									<div
										className={`bg-green-300 cursor-pointer ${statusFilter === JobApplicationStatus.INTERVIEW ? "ring-2 ring-green-500" : ""}`}
										style={{ width: `${interviewPercentage}%` }}
										onClick={() => handleStatusBarClick("INTERVIEW")}
									/>
								</TooltipTrigger>
								<TooltipContent className="px-2 py-1 text-xs" showArrow={true}>
									Interview: {interviewCount}
								</TooltipContent>
							</Tooltip>
						)}
						{rejectionCount > 0 && (
							<Tooltip>
								<TooltipTrigger asChild>
									<div
										className={`bg-red-300 cursor-pointer ${statusFilter === JobApplicationStatus.REJECTED ? "ring-2 ring-red-500" : ""}`}
										style={{ width: `${rejectionPercentage}%` }}
										onClick={() => handleStatusBarClick("rejected")}
									/>
								</TooltipTrigger>
								<TooltipContent className="px-2 py-1 text-xs" showArrow={true}>
									Rejected: {rejectionCount}
								</TooltipContent>
							</Tooltip>
						)}
					</TooltipProvider>
				</div>
				<div className="flex text-xs justify-between mt-2">
					<div
						className={`flex items-center gap-1 cursor-pointer ${statusFilter === JobApplicationStatus.APPLIED ? "font-bold" : ""}`}
						onClick={() => handleStatusBarClick(JobApplicationStatus.APPLIED)}
					>
						<span className="w-3 h-3 bg-blue-300 rounded-lg" />
						<span>Applied: {appliedCount}</span>
					</div>
					<div
						className={`flex items-center gap-1 cursor-pointer ${statusFilter === JobApplicationStatus.SCREENING ? "font-bold" : ""}`}
						onClick={() => handleStatusBarClick(JobApplicationStatus.SCREENING)}
					>
						<span className="w-3 h-3 bg-purple-300 rounded-lg" />
						<span>Screening: {screeningCount}</span>
					</div>
					<div
						className={`flex items-center gap-1 cursor-pointer ${statusFilter === JobApplicationStatus.INTERVIEW ? "font-bold" : ""}`}
						onClick={() => handleStatusBarClick(JobApplicationStatus.INTERVIEW)}
					>
						<span className="w-3 h-3 bg-green-300 rounded-lg" />
						<span>Interview: {interviewCount}</span>
					</div>
					<div
						className={`flex items-center gap-1 cursor-pointer ${statusFilter === JobApplicationStatus.REJECTED ? "font-bold" : ""}`}
						onClick={() => handleStatusBarClick(JobApplicationStatus.REJECTED)}
					>
						<span className="w-3 h-3 bg-red-300 rounded-lg" />
						<span>Rejected: {rejectionCount}</span>
					</div>
				</div>
			</div>

			{/* Upcoming Interviews Card */}
			<div className="bg-white p-4 rounded-lg border">
				<div className="flex justify-between items-center">
					<div>
						<h2 className="text-sm text-gray-500 font-medium">Interviews</h2>
						<p className="text-3xl font-bold">{interviews}</p>
						<p className="text-xs text-gray-500">Next 7 days</p>
					</div>
					<div className="bg-green-100 p-2 rounded-full">
						<Clock className="text-green-600 w-5 h-5" />
					</div>
				</div>
			</div>
		</div>
	);
}

export default SummarySection;
